import { useEffect, useState } from "react"

import Steps from "./steps"
import PlatformSelection from "./platform_selection"
import ServiceSelection from "./service_selection"
import Upsell from "./upsell"
import RecommendedApplets from "./recommended_applets"
import { submitChannels } from "./api"

import { submitPreferredPlatforms } from "./api"
interface TrialConfigUrls {
  proSubscriptionsUrl: string
  proPlusSubscriptionsUrl: string
}

interface TrialConfigAssets {
  proIllustration: string
  proPlusIllustration: string
}

interface Channels {
  brand_color: string
  lrg_monochrome_image_url: string
  module_name: string
  name: string
}

interface Props {
  browserPlatform: string
  channels: Array<Channels>
  trialConfig: {
    assets: TrialConfigAssets
    urls: TrialConfigUrls
  }
  platformPrefImages: { android: string; apple: string }
  urls: {
    exploreUrl: string
  }
  userId: string
  newUserOnboarding: boolean
  pageId: string | null
  paidUser: boolean
}

const OnboardingRecommendations = ({
  browserPlatform,
  channels,
  trialConfig,
  platformPrefImages,
  urls,
  userId,
  newUserOnboarding,
  pageId,
  paidUser,
}: Props) => {
  const [selectedChannels, setSelectedChannels] = useState(new Set<string>())
  const isNewUserOnboarding = newUserOnboarding
  const pathName = newUserOnboarding ? "/onboarding" : "/applet_discovery"
  const STEPS = Object.values(Steps)

  useEffect(() => {
    if (isNewUserOnboarding && browserPlatform !== "desktop") {
      let platform: string | null = null
      switch (browserPlatform) {
        case "ios":
          platform = "iphone"
          break
        case "android":
          platform = "android"
      }
      platform && submitPreferredPlatforms([platform])
    }
  }, [browserPlatform, isNewUserOnboarding])

  const [step, setStep] = useState(
    isNewUserOnboarding && browserPlatform === "desktop" ? Steps.PLATFORM_SELECTION : Steps.SERVICE_SELECTION
  )
  const [stepHistory, setStepHistory] = useState(Array<string>)

  useEffect(() => {
    const validStep = pageId ? STEPS.includes(pageId) : false
    const invalidStep = !validStep && pageId
    const disallowUserNavigation = pageId === "trial" && paidUser

    if (disallowUserNavigation || invalidStep) {
      setStep(Steps.SERVICE_SELECTION)
      const url = pathName + `/${Steps.SERVICE_SELECTION}`
      window.history.replaceState({ turbolinks: true }, "", url)
    } else if (validStep && pageId) {
      setStep(pageId)
    } else {
      const url = pathName + `/${step}`
      window.history.replaceState({ turbolinks: true }, "", url)
    }
  }, [])

  const goBack = () => {
    const previousStep = stepHistory[stepHistory.length - 1]
    setStep(previousStep)

    const history = stepHistory.slice(0, stepHistory.length - 1)
    setStepHistory(history)
  }

  const channelSelected = (moduleName: string) => selectedChannels.has(moduleName)

  const toggleChannel = (moduleName: string) => {
    const newChannels = new Set<string>(selectedChannels)
    if (channelSelected(moduleName)) {
      newChannels.delete(moduleName)
    } else {
      newChannels.add(moduleName)
    }
    setSelectedChannels(newChannels)
  }

  const onPlatformSelected = (): void => {
    setStep(Steps.SERVICE_SELECTION)

    if (!stepHistory.includes(Steps.PLATFORM_SELECTION)) {
      setStepHistory([...stepHistory, Steps.PLATFORM_SELECTION])
    }

    const url = pathName + `/${Steps.SERVICE_SELECTION}`
    window.history.pushState({ turbolinks: true }, "", url)
  }

  const onNext = async () => {
    await submitChannels(Array.from(selectedChannels))
    const nextStep = newUserOnboarding && !paidUser ? Steps.UPSELL : Steps.RECOMMENDED_APPLETS

    setStep(nextStep)

    const url = pathName + `/${nextStep}`
    window.history.pushState({ turbolinks: true }, "", url)

    if (!stepHistory.includes(Steps.SERVICE_SELECTION)) {
      setStepHistory([...stepHistory, Steps.SERVICE_SELECTION])
    }
  }

  const onSkipPro = async () => {
    setStep(Steps.RECOMMENDED_APPLETS)

    if (!stepHistory.includes(Steps.UPSELL)) {
      setStepHistory([...stepHistory, Steps.UPSELL])
    }

    const url = pathName + `/${Steps.RECOMMENDED_APPLETS}`
    window.history.pushState({ turbolinks: true }, "", url)
  }

  const onClickBack = (): void => {
    if (stepHistory.length > 0) {
      goBack()
    }

    window.history.back()
  }

  switch (step) {
    case Steps.PLATFORM_SELECTION:
      return (
        <PlatformSelection
          onPlatformSelected={onPlatformSelected}
          images={platformPrefImages}
          onClickBack={onClickBack}
        />
      )
    case Steps.SERVICE_SELECTION:
      return (
        <ServiceSelection
          channels={channels}
          onNext={onNext}
          showFooter={!!selectedChannels.size}
          channelSelected={channelSelected}
          toggleChannel={toggleChannel}
          newUserOnboarding={newUserOnboarding}
          onClickBack={onClickBack}
        />
      )
    case Steps.UPSELL:
      return <Upsell onSkip={onSkipPro} trialConfig={trialConfig} onClickBack={onClickBack} />
    case Steps.RECOMMENDED_APPLETS:
      return <RecommendedApplets newUserOnboarding={newUserOnboarding} urls={urls} onClickBack={onClickBack} />
    default:
      return null
  }
}

export default OnboardingRecommendations
